import {Card, CardContent, Container, Divider, Stack, Typography} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import cn from "classnames";
import React, {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {MultiSelect} from "react-multi-select-component";
import {useAuth} from "react-oidc-context";
import {useDispatch, useSelector} from "react-redux";
import {useLocation, useParams} from "react-router-dom";
import {
    exportExcel,
    fetchChartData,
    getAllGasStations,
    getAllGasStationsForGSAdmin,
    getAllParkingZones,
    getAllParkingZonesForPZAdmin,
} from "../../actions/report";
import Button from "../../common/button/button";
import SectionHeader from "../../common/card/SectionHeader";
import ChartBar from "../../common/chart/bar"
import DateRangePicker, {DateRangePresetsType} from "../../common/dateRangePicker/dateRangePickerWithPreset";
import EmailReportToggle from "../../common/emailReportToggle/EmailReportToggle";
import MultiSelectCheckbox from "../../common/multiselectCheckBox/MultiSelectCheckbox.tsx";
import ControlLabelTypography from "../../common/typography/ControlLabelTypography";
import {ROLES} from "../../consts";
import GasStationRankingTable from "../../containers/report/GasStationRankingTable";
import GsLabelRankingTable from "../../containers/report/GsLabelRankingTable";
import MapWidget from "../../containers/report/MapWidget";
import {useGlobalState} from "../../GlobalContext";
import {ALL_COUNTRY, colors, supportedCountries} from '../../globals';
import {useGasStationMapData} from "../../hooks/useGasStationMapData";
import keycloak from "../../keycloak";
import LoadingIndicator from "../../reusable/loadingIndicator";
import {toolbarStyles, useStyles} from "../../reusable/styles";
import Sidebar from "../../sidebar/sidebar";
import {getEndOfMonth, getStartOfMonth} from '../../utils/dateUtils';
import {currencyCode} from "../../utils/formatter";
import ActivityWidget from "./ActivityWidget";
import PerformanceWidget from "./PerformanceWidget";
import Style from "./report.module.css";


export default function Report() {
    const auth = useAuth();

    const { reportType } = useParams();
    const location = useLocation();
    // Create URLSearchParams object from location.search
    const queryParams = new URLSearchParams(location.search);
    const driveOffReport = reportType === "DRIVE_OFFS";

    const [selectedPZ, setSelectedPZ] = useState([]);
    const [selectedGSIds, setSelectedGSIds] = useState([]);

    const classes = useStyles();
    const {isLoading} = useSelector((state) => state.loader);
    const { t } = useTranslation();
    const classesForm = toolbarStyles();
    const dispatch = useDispatch();
    const { parkingZones } = useSelector((state) => state.report);
    const { gsZones } = useSelector((state) => state.report);
    const { chartData } = useSelector((state) => state.report);
    const initialRender = useRef(true);
    const { state, updateState } = useGlobalState();    
    const today = new Date();
    const isAllCountries = state.country == ALL_COUNTRY;    
    const [dateRange, setDateRange] = useState({
      from: getStartOfMonth(today),
      to: getEndOfMonth(today),
      preset: DateRangePresetsType.CurrentMonth
    });
    const handleDateRangeChange = (newValue) => {
    console.log("New Date Range:", newValue);
      setDateRange(newValue);
    };

    const { data: gasStationMapData } = useGasStationMapData({
      selectedGSZIds: selectedGSIds,
      dateRange: dateRange
    });

    const locations = Array.isArray(gasStationMapData)
            ? gasStationMapData.map((item, index) => ({
                id: item.gasStationId || index + 1,
                number: item.gasStationNumber,
                name: item.gasStationName,
                coordinates: [
                        parseFloat(item.marker?.longitude || item.marker?.lng),
                        parseFloat(item.marker?.latitude || item.marker?.lat)
                      ],
                type: 'gas_station',
                driveOffCount: item.driveoffCountInPeriod,
                activityType: item.activityType
              }))
            : [];

    // Handler for MultiSelectCheckbox
    const handleGSSelectionChange = (selectedIds) => {
        setSelectedGSIds(selectedIds);
        // Convert IDs back to the format expected by the rest of the component
        const selectedStations = gsZones
            .filter(zone => selectedIds.includes(zone.id))
            .map(zone => ({
                value: zone.number,
                label: `${zone.number} - ${zone.name}`
            }));
        setSelectedPZ(selectedStations);
    };

  useEffect(() => {
      if (keycloak.hasRealmRole(ROLES.SYSADMIN, auth)) {
          if (reportType === "VIOLATIONS") {
              dispatch(getAllParkingZones(state.country));
          } else {
              dispatch(getAllGasStations(state.country));
          }
      } else {
          if (reportType === "VIOLATIONS") {
              dispatch(getAllParkingZonesForPZAdmin(state.country));
          } else {
              dispatch(getAllGasStationsForGSAdmin(state.country));
          }
      }
    }, [dispatch, reportType, state.country]);

    useEffect(() => {
        if (initialRender.current) {
          initialRender.current = false;
        } else {
          if (selectedPZ.length > 0) {
            dispatch(fetchChartData(selectedPZ.map(zone => zone.value), reportType, state.country));
          } else {
              // Reset chart data when no zones are selected
              dispatch({ type: 'RESET_CHART_DATA' });
          }
        }
    }, [selectedPZ, reportType, dispatch]);

    useEffect(() => {
        if (reportType === "VIOLATIONS" && parkingZones.length > 0) {
            setSelectedPZ(
                parkingZones
                    .filter(zone => zone.countryCode === state.country || isAllCountries)
                    .map(zone => ({
                        value: zone.parkingZoneNumber,
                        label: `${zone.parkingZoneNumber} - ${zone.parkingZoneName}`
                    }
            )))
        }
    }, [parkingZones, state.country]);

    useEffect(() => {
        if (reportType === "DRIVE_OFFS" && gsZones.length > 0) {
            const storageKey = `${state.country}:selectedGSIds`;
            const storedIds = localStorage.getItem(storageKey);
            const availableIds = gsZones
                .filter(zone => zone.countryCode === state.country || isAllCountries)
                .map(zone => zone.id);

            let selectedIds;
            if (isAllCountries || !storedIds) {
                selectedIds = availableIds;
            } else {
                const parsedIds = JSON.parse(storedIds);
                selectedIds = parsedIds.filter(id => availableIds.includes(id));
                if (selectedIds.length === 0) {
                    selectedIds = availableIds;
                }
            }

            setSelectedGSIds(selectedIds);

            const selectedStations = gsZones
                .filter(zone => selectedIds.includes(zone.id))
                .map(zone => ({
                    value: zone.number,
                    label: `${zone.number} - ${zone.name}`
                }));
            setSelectedPZ(selectedStations);
        }
    }, [gsZones, state.country]);

    // Separate effect only for user-initiated changes
    useEffect(() => {
        if (reportType === "DRIVE_OFFS" && selectedGSIds.length > 0) {
            const storageKey = `${state.country}:selectedGSIds`;
            localStorage.setItem(storageKey, JSON.stringify(selectedGSIds));
        }
    }, [selectedGSIds]);


    let pzOptions;
    if (reportType === "VIOLATIONS") {
        pzOptions = parkingZones
            .filter((zone) => {
            const match = zone.countryCode === state.country;
            const result = isAllCountries || match;
            return result;
            })
            .map(zone => ({
                value: zone.parkingZoneNumber,
                label: `${zone.parkingZoneNumber} - ${zone.parkingZoneName}`
            }));
    } else {
        pzOptions = gsZones
            .filter((zone) => {
            const match = zone.countryCode === state.country;
            const result = isAllCountries || match;
            return result;
            })
            .map(zone => ({
                value: zone.number,
                label: `${zone.number} - ${zone.name}`
            }));
    }

    const handleExportExcel = async () => {
        await dispatch(
            exportExcel(
                selectedPZ.map(zone => zone.value),
                reportType,
                localStorage.getItem("beppoSelectedLanguage"),
                state.country
            ))
    };

      const labels = chartData.chartLabels;
      const datasetsForTickets = [
        {
          label: t("Reports.Open"),
          backgroundColor: colors.blue,
          borderColor: colors.blue,
          borderWidth: 1,
          data: chartData.numberOfTickets ? chartData.numberOfTickets.OPEN : [],
        },
        {
          label: t("Reports.Paid"),
          backgroundColor: colors.green,
          borderColor: colors.green,
          borderWidth: 1,
          data: chartData.numberOfTickets ? chartData.numberOfTickets.PAID : [],
        },
        {
          label: t("Reports.Closed"),
          backgroundColor: colors.gray,
          borderColor: colors.gray,
          borderWidth: 1,
          data: chartData.numberOfTickets ? chartData.numberOfTickets.CLOSED : [],
        },
        {
          label: t("Reports.Cancelled"),
          backgroundColor: colors.red,
          borderColor: colors.red,
          borderWidth: 1,
          data: chartData.numberOfTickets ? chartData.numberOfTickets.CANCELLED : [],
        },
      ];

      const datasetsForCompensations = [
        {
          label: t("Reports.Open"),
          backgroundColor: colors.blue,
          borderColor: colors.blue,
          borderWidth: 1,
          data: chartData.compensationValue ? chartData.compensationValue.OPEN : [],
        },
        {
          label: t("Reports.Paid"),
          backgroundColor: colors.green,
          borderColor: colors.green,
          borderWidth: 1,
          data: chartData.compensationValue ? chartData.compensationValue.PAID : [],
        },
        {
          label: t("Reports.Closed"),
          backgroundColor: colors.gray,
          borderColor: colors.gray,
          borderWidth: 1,
          data: chartData.compensationValue ? chartData.compensationValue.CLOSED : [],
        },
        {
          label: t("Reports.Cancelled"),
          backgroundColor: colors.red,
          borderColor: colors.red,
          borderWidth: 1,
          data: chartData.compensationValue ? chartData.compensationValue.CANCELLED : [],
        },
      ];


    const selectedZoneNames = selectedPZ.map(zone => {
        return `${zone.label}`;
    });

    const currency = currencyCode(state.country);

    let titleNumberOfTickets;
    let titleCompensation;
    if (selectedPZ.length > 0) {
        if (selectedPZ.length > 3) {
            titleNumberOfTickets = <span dangerouslySetInnerHTML={{__html: `<b>${t("Reports.NoOfTickets")}</b> | ${selectedZoneNames.slice(0, 3).join(', ')}, ...`}} />;
            titleCompensation = <span dangerouslySetInnerHTML={{__html: `<b>${reportType === "VIOLATIONS" ? t("Reports.CompensationValueCHF", { currency }) : t("Reports.FuelValueCHF", { currency })}</b> | ${selectedZoneNames.slice(0, 3).join(', ')}, ...`}} />;
        } else {
            titleNumberOfTickets = <span dangerouslySetInnerHTML={{__html: `<b>${t("Reports.NoOfTickets")}</b> | ${selectedZoneNames.slice(0, 3).join(', ')}`}} />;
            titleCompensation = <span dangerouslySetInnerHTML={{__html: `<b>${reportType === "VIOLATIONS" ? t("Reports.CompensationValueCHF", { currency }) : t("Reports.FuelValueCHF", { currency })}</b> | ${selectedZoneNames.slice(0, 3).join(', ')}`}} />;
        }
    } else {
        titleNumberOfTickets = <span dangerouslySetInnerHTML={{__html: `<b>${t("Reports.NoOfTickets")}</b>`}} />;
        titleCompensation = <span dangerouslySetInnerHTML={{__html: `<b>${reportType === "VIOLATIONS" ? t("Reports.CompensationValueCHF", { currency }) : t("Reports.FuelValueCHF", { currency })}</b>`}} />;
    }

    return (
    <div className={classes.root}>
      <>
        <Sidebar/>
        <main className={classes.content}>
          <div className={classes.appBarSpacer}/>
          <Container maxWidth={false} sx={{ paddingY: 2, borderTop: "1px solid #888", height: "100%" }}>
              {isLoading && <LoadingIndicator/>}

              <Grid2 direction="row" container spacing={2} columnSpacing={0} sx={{mb: 1}}>
                  <Grid2 md={12} xl={4}>
                      <Typography variant="h5" sx={{fontFamily: "Gilroy-Bold", fontWeight: 500}}>
                          {reportType === "VIOLATIONS"
                              ? t("Reports.ParkingViolations")
                              : reportType === "DRIVE_OFFS"
                                  ? t("GasStations.GasStations") + " & " + t("Reports.DriveOffs")
                                  : ""}
                      </Typography>
                  </Grid2>

                  {/* Right section - Controls */}
                  <Grid2 md={12} xl={8}>
                      <Stack
                          direction="row"
                          spacing={2}
                          useFlexGap
                          alignItems="flex"
                          justifyContent="flex-end"
                      >
                          {driveOffReport && (
                              <>
                                  <Stack direction="row" alignItems="center">
                                      <ControlLabelTypography>
                                          {t("Reports.Dashboard.SelectDatePeriod")}
                                      </ControlLabelTypography>
                                      <DateRangePicker
                                          value={dateRange}
                                          onValueChange={handleDateRangeChange}
                                          placeholder={t("Reports.Dashboard.SelectDatePeriod")}
                                      />
                                  </Stack>
                                  <Stack direction="row" alignItems="center">
                                      <ControlLabelTypography>
                                          {t("Reports.SelectGasStation")}
                                      </ControlLabelTypography>
                                      <div className="tailwind-scope">
                                          <MultiSelectCheckbox
                                              data={gsZones.filter(zone => zone.countryCode === state.country || isAllCountries)}
                                              value={selectedGSIds}
                                              onChange={handleGSSelectionChange}
                                          />
                                      </div>
                                  </Stack>
                              </>
                          )}

                          {!driveOffReport && (
                              <Stack direction="row" alignItems="center" spacing={2} minWidth={200}>
                                  <ControlLabelTypography>
                                      {reportType === "VIOLATIONS"
                                          ? t("Reports.SelectParkingZones")
                                          : reportType === "DRIVE_OFFS"
                                              ? t("Reports.SelectGasStation")
                                              : ""}
                                  </ControlLabelTypography>
                                  <MultiSelect
                                      options={pzOptions}
                                      value={selectedPZ}
                                      onChange={setSelectedPZ}
                                      labelledBy="Select..."
                                      className={Style.multiSelectContainer}
                                  />
                                  <Button
                                      style={{ width: '100px'}}
                                      buttonStyle={{ height: "40px" }}
                                      className={Style.exportButton}
                                      text={t("Reports.Export")}
                                      onClick={() => handleExportExcel()}
                                  />
                              </Stack>

                          )}
                          {driveOffReport && (
                              <Stack direction="row" alignItems="center" justifyContent="flex-end">
                                  <EmailReportToggle/>
                                  <Button
                                      style={{ width: '100px'}}
                                      buttonStyle={{ height: "40px" }}
                                      className={Style.exportButton}
                                      text={t("Reports.Export")}
                                      onClick={() => handleExportExcel()}
                                  />
                              </Stack>
                          )}
                      </Stack>
                  </Grid2>
              </Grid2>
            <div className={cn(Style.scrollableDiv, "")}>
                {/* DO Dashboard    */}
                {driveOffReport &&
                    <Stack spacing={2} sx={{height: "100%", width: "100%", paddingBottom: "20px"}}>
                        <Grid2 container spacing={2} sx={{height: "100%", width: "100%"}}>
                            <Grid2 container spacing={2} columnSpacing={0} xl={7} md={6} sx={{width: "100%"}}>
                                <Stack
                                    spacing={2}
                                    sx={{height: "100%", width: "100%"}}
                                >
                                    <ActivityWidget
                                        selectedGSZIds={selectedGSIds}
                                        dateRange={dateRange}
                                    />
                                    <PerformanceWidget
                                        selectedGSZIds={selectedGSIds}
                                        dateRange={dateRange}
                                    />
                                </Stack>
                            </Grid2>
                            <Grid2 xl={5} md={6} sx={{paddingTop: "0"}}>
                                <Card sx={{height: "596px"}}>
                                    <CardContent sx={{}}>
                                        <SectionHeader
                                            title={t("Reports.Dashboard.MapTitle")}
                                            subtitle={t("Reports.Dashboard.InThisPeriod")}
                                        />
                                        <MapWidget
                                        countryCodes={
                                            state.country === "ALL"
                                            ? Object.keys(supportedCountries).filter(code => code !== "ALL")
                                            : [state.country]
                                        }
                                        locations={locations}
                                        />
                                    </CardContent>
                                </Card>
                            </Grid2>
                        </Grid2>
                        <Card>
                            <CardContent>
                                <SectionHeader
                                    title={t("Reports.Dashboard.GS-DO-TableTitle")}
                                    subtitle={t("Reports.Dashboard.GS-DO-TableSubtitle")}
                                />
                                <GasStationRankingTable selectedGSIds={selectedGSIds} />
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent>
                                <SectionHeader
                                    title={t("Reports.Dashboard.Region-DO-TableTitle")}
                                    subtitle={t("Reports.Dashboard.GS-DO-TableSubtitle")}
                                />
                                <GsLabelRankingTable selectedGSIds={selectedGSIds} />
                            </CardContent>
                        </Card>

                    </Stack>}
                    <Stack spacing={2} sx={{height: "100%", width: "100%", paddingBottom: "20px"}}>
                        <Card>
                            <CardContent>
                                <SectionHeader
                                    title={driveOffReport ? t("Reports.Dashboard.NumberOfDO") : t("Reports.Dashboard.NumberOfTickets")}
                                    subtitle={t("Reports.Dashboard.PerMonth")}
                                />
                                <ChartBar labels={labels} data={datasetsForTickets} title={null}/>
                            </CardContent>
                        </Card>
                        <Card>
                            <CardContent>
                                <SectionHeader
                                    title={driveOffReport ? t("Reports.Dashboard.ValueOfDO") : t("Reports.Dashboard.ValueOfTickets")}
                                    subtitle={t("Reports.Dashboard.PerMonth")}
                                />
                                <ChartBar labels={labels} data={datasetsForCompensations} title={null}/>
                            </CardContent>
                        </Card>
                    </Stack>
            </div>
            </Container>
        </main>
    </>
    </div>
  );
}